import type {
  AppState,
  NotairesMonthlyOutput,
  NotaireStaff,
  NotaireStaffType,
  Salarie,
} from '../types';
import { getCA } from './calcEngine';
import { getTotalMonths } from './formatters';

/** Préfixe stable pour identifier les salariés générés automatiquement par le module Notaires. */
export const NOTAIRE_AUTO_ID_PREFIX = 'notaire-auto-';

/** True si le salarié a été généré par le module notaires (non éditable manuellement). */
export function isNotaireAutoSalarie(s: { id: string }): boolean {
  return typeof s.id === 'string' && s.id.startsWith(NOTAIRE_AUTO_ID_PREFIX);
}

/**
 * Moteur notaires — calcule, pour chaque mois de la projection :
 *   - le nombre de notaires salariés / Head of Ops / assistants actifs,
 *   - la capacité interne et la répartition commandes internes / partenaires,
 *   - les recrutements déclenchés ce mois (arrivée effective = mi + delaiRecrutement),
 *   - les coûts détaillés (salaires fixes + commissions partenaires),
 *   - le % du CA que représente le coût total notaires.
 *
 * Règles :
 *   - Les notaires salariés absorbent le flux de base (capacité = nbNotaires × capaciteParNotaire).
 *     Les commandes qui dépassent vont aux partenaires (coût variable par commande).
 *   - Si capacité = 0 et qu'il y a au moins 1 commande, on recrute d'urgence.
 *   - Sinon, si la charge (commandesTotal / capaciteInterne) ≥ seuilRecrutementPct et
 *     qu'aucun notaire n'est déjà dans le pipeline, on déclenche un recrutement.
 *   - Head of Notary Operations : 1 seul poste, recruté automatiquement dès que le nombre
 *     total de notaires (actifs + en pipeline) atteint seuilHeadOps.
 *   - Assistants notaire : 1 pour ratioAssistant notaires salariés actifs. L'algo en
 *     recrute autant qu'il en manque.
 *   - Délai de recrutement : le poste déclenché au mois M arrive au mois M + delai.
 *     Les coûts commencent à l'arrivée effective.
 */

function sumAt<T>(arr: (T | undefined)[], key: (v: T) => number, from: number): number {
  let s = 0;
  for (let i = from; i < arr.length; i++) {
    const v = arr[i];
    if (v) s += key(v);
  }
  return s;
}

type PipelineBucket = { notaire: number; headOps: number; assistant: number };

function emptyBucket(): PipelineBucket {
  return { notaire: 0, headOps: 0, assistant: 0 };
}

/**
 * Retourne la durée totale de la projection en mois (somme des 3 exercices).
 */
export function getNotairesTotalMonths(state: AppState): number {
  const { exercice1Duree, exercice2Duree, exercice3Duree } = state.meta ?? {};
  return getTotalMonths(exercice1Duree, exercice2Duree, exercice3Duree);
}

/**
 * Calcule la timeline notaires mois par mois pour un `AppState` donné.
 * Pure function : aucune mutation du state.
 *
 * Si `config.enabled === false` OU panierMoyen <= 0, on renvoie une timeline
 * avec tous les postes à 0 (désactivation totale du module).
 */
export function computeNotairesTimeline(state: AppState): NotairesMonthlyOutput[] {
  const totalMi = getNotairesTotalMonths(state);
  const config = state.notairesConfig;
  const timeline: NotairesMonthlyOutput[] = [];

  // Module désactivé → on renvoie quand même une timeline cohérente (0 partout).
  if (!config || !config.enabled || !Number.isFinite(config.panierMoyen) || config.panierMoyen <= 0) {
    for (let mi = 0; mi < totalMi; mi++) {
      const ca = getCA(state, mi);
      timeline.push({
        mi,
        ca,
        commandesTotal: 0,
        notairesActifs: 0,
        capaciteInterne: 0,
        commandesInternes: 0,
        commandesPartenaires: 0,
        headOpsActifs: 0,
        assistantsActifs: 0,
        recrutementsMois: [],
        coutNotaires: 0,
        coutPartenaires: 0,
        coutHeadOps: 0,
        coutAssistants: 0,
        coutTotal: 0,
        pctCA: 0,
      });
    }
    return timeline;
  }

  const delai = Math.max(0, Math.floor(config.delaiRecrutement ?? 1));
  const pipeline: PipelineBucket[] = Array.from({ length: totalMi + delai + 1 }, emptyBucket);

  let notaires = Math.max(0, Math.floor(config.notairesInitiaux ?? 0));
  let headOps = 0;
  let assistants = 0;

  for (let mi = 0; mi < totalMi; mi++) {
    // 1) Arrivées du mois
    const arr = pipeline[mi];
    notaires += arr.notaire;
    headOps += arr.headOps;
    assistants += arr.assistant;

    // 2) Demande
    const ca = getCA(state, mi);
    const commandesTotal = config.panierMoyen > 0 ? Math.ceil(ca / config.panierMoyen) : 0;
    const capaciteInterne = notaires * Math.max(0, config.capaciteParNotaire);
    const commandesInternes = Math.min(commandesTotal, capaciteInterne);
    const commandesPartenaires = Math.max(0, commandesTotal - commandesInternes);

    // 3) Déclenchement des recrutements
    const recrutementsMois: { type: NotaireStaffType; arriveeMi: number }[] = [];
    const arriveeMi = mi + delai;

    // a) Notaires salariés — double garde : saturation ET rentabilité économique
    //
    //    Règle saturation : on n'envisage d'embaucher que si la capacité interne est saturée
    //    (≥ seuil %) ou nulle alors qu'il y a une demande.
    //
    //    Règle rentabilité (stricte) : au MOIS D'ARRIVÉE projeté, les commissions partenaires
    //    que le nouveau notaire absorberait doivent déjà dépasser son salaire chargé.
    //    Autrement dit : overflow_arrivée × coûtParCmd ≥ salaireNotaire.
    //    → Tant que le partenaire resterait moins cher au mois d'arrivée, on attend.
    //    C'est plus conservateur que regarder une moyenne future, mais ça reflète l'intuition
    //    qu'il ne faut pas provisionner un salaire si l'alternative partenaire coûte moins.
    const pendingNotaires = sumAt(pipeline, (b) => b.notaire, mi + 1);
    const fillRate = capaciteInterne > 0 ? (commandesTotal / capaciteInterne) * 100 : Infinity;
    const saturationOk =
      (capaciteInterne === 0 && commandesTotal > 0) ||
      (capaciteInterne > 0 && fillRate >= config.seuilRecrutementPct);

    let economicOk = false;
    if (saturationOk && arriveeMi < totalMi) {
      const coutParCmd = Math.max(0, config.panierMoyen) * Math.max(0, config.coutPartenairePct) / 100;
      const capaciteSupplementaire = Math.max(0, config.capaciteParNotaire);
      // Capacité déjà planifiée au mois d'arrivée (actifs + pending), hors le notaire évalué.
      const capaciteArriveeExcl = (notaires + sumAt(pipeline, (b) => b.notaire, mi + 1)) * capaciteSupplementaire;
      const caArrivee = getCA(state, arriveeMi);
      const cmdArrivee = config.panierMoyen > 0 ? Math.ceil(caArrivee / config.panierMoyen) : 0;
      const overflowArrivee = Math.max(0, cmdArrivee - capaciteArriveeExcl);
      const absorbable = Math.min(overflowArrivee, capaciteSupplementaire);
      const economiesArrivee = absorbable * coutParCmd;
      economicOk = economiesArrivee >= Math.max(0, config.salaireNotaire);
    }

    const needsNotaire = saturationOk && economicOk;
    if (needsNotaire && pendingNotaires === 0 && arriveeMi < pipeline.length) {
      pipeline[arriveeMi].notaire += 1;
      recrutementsMois.push({ type: 'notaire', arriveeMi });
    }

    // b) Head of Notary Operations : 1 unique poste
    const notairesFutur = notaires + sumAt(pipeline, (b) => b.notaire, mi + 1);
    const pendingHeadOps = sumAt(pipeline, (b) => b.headOps, mi + 1);
    if (
      notairesFutur >= Math.max(1, config.seuilHeadOps) &&
      headOps + pendingHeadOps < 1 &&
      arriveeMi < pipeline.length
    ) {
      pipeline[arriveeMi].headOps += 1;
      recrutementsMois.push({ type: 'head_ops', arriveeMi });
    }

    // c) Assistants : 1 pour ratioAssistant notaires
    const ratio = Math.max(1, Math.floor(config.ratioAssistant ?? 6));
    const requiredAssistants = Math.floor(notaires / ratio);
    const pendingAssistants = sumAt(pipeline, (b) => b.assistant, mi + 1);
    const gap = requiredAssistants - (assistants + pendingAssistants);
    if (gap > 0 && arriveeMi < pipeline.length) {
      pipeline[arriveeMi].assistant += gap;
      for (let k = 0; k < gap; k++) {
        recrutementsMois.push({ type: 'assistant', arriveeMi });
      }
    }

    // 4) Coûts du mois (basés sur les effectifs actifs APRÈS arrivées du mois)
    const coutNotaires = notaires * Math.max(0, config.salaireNotaire);
    const coutParCommandePartenaire =
      Math.max(0, config.panierMoyen) * Math.max(0, config.coutPartenairePct) / 100;
    const coutPartenaires = commandesPartenaires * coutParCommandePartenaire;
    const coutHeadOps = headOps * Math.max(0, config.salaireHeadOps);
    const coutAssistants = assistants * Math.max(0, config.salaireAssistant);
    const coutTotal = coutNotaires + coutPartenaires + coutHeadOps + coutAssistants;
    const pctCA = ca > 0 ? (coutTotal / ca) * 100 : 0;

    timeline.push({
      mi,
      ca,
      commandesTotal,
      notairesActifs: notaires,
      capaciteInterne,
      commandesInternes,
      commandesPartenaires,
      headOpsActifs: headOps,
      assistantsActifs: assistants,
      recrutementsMois,
      coutNotaires,
      coutPartenaires,
      coutHeadOps,
      coutAssistants,
      coutTotal,
      pctCA,
    });
  }

  return timeline;
}

/**
 * Dérive la liste individuelle des membres de l'équipe notaires à partir de la timeline.
 * Chaque notaire salarié (initial ou recruté), chaque Head of Ops, chaque assistant a sa propre ligne.
 * Triée par date d'arrivée croissante, puis par type (notaire → head_ops → assistant).
 */
export function buildNotairesStaffList(
  state: AppState,
  timeline: NotairesMonthlyOutput[],
): NotaireStaff[] {
  const config = state.notairesConfig;
  if (!config || !config.enabled) return [];

  const staff: NotaireStaff[] = [];
  const counters: Record<NotaireStaffType, number> = { notaire: 0, head_ops: 0, assistant: 0 };
  const costOf: Record<NotaireStaffType, number> = {
    notaire: Math.max(0, config.salaireNotaire),
    head_ops: Math.max(0, config.salaireHeadOps),
    assistant: Math.max(0, config.salaireAssistant),
  };
  const labelOf: Record<NotaireStaffType, string> = {
    notaire: 'Notaire',
    head_ops: 'Head of Notary Ops',
    assistant: 'Assistant notaire',
  };

  // Effectif initial (M0) — uniquement les notaires salariés (seul compte initial prévu).
  const initialNotaires = Math.max(0, Math.floor(config.notairesInitiaux ?? 0));
  for (let i = 0; i < initialNotaires; i++) {
    counters.notaire += 1;
    staff.push({
      id: `notaire-init-${i}`,
      type: 'notaire',
      nom: `${labelOf.notaire} #${counters.notaire}`,
      moisArrivee: 0,
      coutMensuel: costOf.notaire,
      initial: true,
    });
  }

  // Recrutements issus de la timeline (ordre déterministe : on parcourt mois par mois).
  for (const row of timeline) {
    for (const r of row.recrutementsMois) {
      counters[r.type] += 1;
      staff.push({
        id: `${r.type}-mi${row.mi}-${counters[r.type]}`,
        type: r.type,
        nom: `${labelOf[r.type]} #${counters[r.type]}`,
        moisArrivee: r.arriveeMi,
        coutMensuel: costOf[r.type],
        initial: false,
      });
    }
  }

  staff.sort((a, b) => {
    if (a.moisArrivee !== b.moisArrivee) return a.moisArrivee - b.moisArrivee;
    const order: Record<NotaireStaffType, number> = { notaire: 0, head_ops: 1, assistant: 2 };
    return order[a.type] - order[b.type];
  });

  return staff;
}

/**
 * Cache mémoïsé : (stateRef, notairesConfigRef, totalMi) → timeline.
 * Évite de recalculer la timeline à chaque appel depuis calcEngine.getChargesOpex pour chaque mi.
 */
const timelineCache = new WeakMap<AppState, NotairesMonthlyOutput[]>();

export function getNotairesTimelineCached(state: AppState): NotairesMonthlyOutput[] {
  const cached = timelineCache.get(state);
  if (cached) return cached;
  const fresh = computeNotairesTimeline(state);
  timelineCache.set(state, fresh);
  return fresh;
}

/**
 * Coût notaires total (€) pour un mois donné (salariés + partenaires + support).
 * NE PLUS injecter dans `getChargesOpex` — les salariés passent par `state.salaries` (virtuels)
 * et seule la sous-traitance partenaires reste dans les charges opérationnelles.
 * Conservé pour compatibilité / affichage agrégé.
 */
export function getNotairesCoutMois(state: AppState, mi: number): number {
  const config = state.notairesConfig;
  if (!config || !config.enabled) return 0;
  const timeline = getNotairesTimelineCached(state);
  return timeline[mi]?.coutTotal ?? 0;
}

/**
 * Sous-traitance notaires du mois — commissions versées aux notaires partenaires pour les
 * commandes qui dépassent la capacité interne.
 * C'est cette part seule qui est injectée dans `getChargesOpex` (coût variable externe).
 */
export function getNotairesSousTraitanceMois(state: AppState, mi: number): number {
  const config = state.notairesConfig;
  if (!config || !config.enabled) return 0;
  const timeline = getNotairesTimelineCached(state);
  return timeline[mi]?.coutPartenaires ?? 0;
}

/**
 * Détail sous-traitance du mois (utilisé pour affichage dans la synthèse).
 */
export function getNotairesSousTraitanceDetail(
  state: AppState,
  mi: number,
): { nbCommandesPartenaires: number; coutParCommande: number; total: number } {
  const config = state.notairesConfig;
  if (!config || !config.enabled) return { nbCommandesPartenaires: 0, coutParCommande: 0, total: 0 };
  const timeline = getNotairesTimelineCached(state);
  const row = timeline[mi];
  const coutParCommande =
    Math.max(0, config.panierMoyen) * Math.max(0, config.coutPartenairePct) / 100;
  return {
    nbCommandesPartenaires: row?.commandesPartenaires ?? 0,
    coutParCommande,
    total: row?.coutPartenaires ?? 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Dérivation des salariés virtuels à partir du staff notaires.
// Ces Salarie[] sont injectés dans state.salaries via l'action NOTAIRES_SALARIES_SYNC
// pour apparaître naturellement dans la table salariés et dans getMasseSalariale.
// ─────────────────────────────────────────────────────────────────────────────

const POSTE_LABEL: Record<NotaireStaffType, string> = {
  notaire: 'Notaire salarié',
  head_ops: 'Head of Notary Ops',
  assistant: 'Assistant notaire',
};

const VIRTUAL_NOTES = 'Ligne générée automatiquement par le module Notaires. Modifiez la config notaires pour ajuster.';

/**
 * Convertit le staff notaires (dérivé de la timeline) en un tableau de Salarie.
 *
 * Chaque entrée utilise un ID stable préfixé `notaire-auto-` afin que le reducer puisse
 * détecter les lignes gérées par le module et les remplacer atomiquement lors d'un resync.
 *
 * Coût employeur = brut * (1 + customTaux/100). On met tauxPatronal='custom' + customTaux=0
 * pour que `brut` corresponde directement au coût employeur annoncé dans la config.
 */
export function buildNotairesVirtualSalaries(
  state: AppState,
  totalMi?: number,
): Salarie[] {
  const config = state.notairesConfig;
  if (!config || !config.enabled) return [];

  const mi = totalMi ?? getNotairesTotalMonths(state);
  const timeline = computeNotairesTimeline(state);
  const staff = buildNotairesStaffList(state, timeline);
  if (staff.length === 0) return [];

  return staff.map<Salarie>((s) => ({
    id: `${NOTAIRE_AUTO_ID_PREFIX}${s.id}`,
    nom: s.nom,
    poste: POSTE_LABEL[s.type],
    categorieId: null,
    moisEntree: Math.max(0, s.moisArrivee),
    moisSortie: null,
    brut: s.coutMensuel,
    tauxPatronal: 'custom',
    customTaux: 0,
    primes: Array(mi).fill(0),
    brutsReels: Array(mi).fill(null) as (number | null)[],
    notes: VIRTUAL_NOTES,
  }));
}

/**
 * Compare 2 listes de salariés virtuels notaires et renvoie true si elles sont identiques
 * (même nombre, mêmes IDs, mêmes (moisEntree, brut, poste)). Sert à éviter les dispatch
 * inutiles du hook de sync.
 */
export function areNotairesSalariesEqual(a: Salarie[], b: Salarie[]): boolean {
  if (a.length !== b.length) return false;
  // Comparaison ordre-insensible sur les champs qui peuvent changer.
  const toKey = (s: Salarie) =>
    `${s.id}|${s.moisEntree}|${s.brut}|${s.poste}|${s.nom}`;
  const setA = new Set(a.map(toKey));
  for (const s of b) if (!setA.has(toKey(s))) return false;
  return true;
}

/**
 * Helpers UI — agrégats par exercice fiscal.
 */
export function aggregateNotairesByExercice(
  timeline: NotairesMonthlyOutput[],
  exRanges: [number, number][],
): {
  ex: number;
  commandes: number;
  coutTotal: number;
  ca: number;
  pctCA: number;
}[] {
  return exRanges.map(([start, end], ex) => {
    let commandes = 0;
    let coutTotal = 0;
    let ca = 0;
    for (let mi = start; mi <= end; mi++) {
      const row = timeline[mi];
      if (!row) continue;
      commandes += row.commandesTotal;
      coutTotal += row.coutTotal;
      ca += row.ca;
    }
    return {
      ex,
      commandes,
      coutTotal,
      ca,
      pctCA: ca > 0 ? (coutTotal / ca) * 100 : 0,
    };
  });
}

export type { NotaireStaff };
